import { useEffect, useState } from "react";

export interface PageSlice<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export function paginate<T>(
  items: T[],
  requestedPage: number,
  requestedPageSize: number,
): PageSlice<T> {
  const pageSize = Math.max(1, Math.floor(requestedPageSize));
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(
    totalPages,
    Math.max(1, Math.floor(requestedPage) || 1),
  );
  const offset = (page - 1) * pageSize;
  return {
    items: items.slice(offset, offset + pageSize),
    page,
    pageSize,
    total,
  };
}

/**
 * `resetKey` returns to page 1 whenever the list identity changes — a new
 * filter or a freshly previewed plan — so the page number cannot outlive the
 * list it indexed into and snap back once the list grows again.
 */
export function usePagination<T>(
  items: T[],
  pageSize: number,
  resetKey?: unknown,
): [PageSlice<T>, (page: number) => void] {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [resetKey]);
  return [paginate(items, page, pageSize), setPage];
}

export function PaginationControls<T>({
  label,
  onPageChange,
  slice,
}: {
  label: string;
  onPageChange: (page: number) => void;
  slice: PageSlice<T>;
}) {
  const { page, pageSize, total } = slice;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(total, page * pageSize);
  return (
    <footer className="directory-footer pagination-footer">
      <span>
        {start}–{end} of {total} {label}
      </span>
      {totalPages > 1 && (
        <nav aria-label={`${label} pagination`} className="pagination-controls">
          <button
            className="button button--quiet button--compact"
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
            type="button"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            className="button button--quiet button--compact"
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
            type="button"
          >
            Next
          </button>
        </nav>
      )}
    </footer>
  );
}
